import React, { useEffect, useRef, useState } from 'react'
import ImageRecommendations from 'components/modals/image-recommendations-modal'
import ImageRecommendationsButton from 'components/modals/image-recommendations-modal/button'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
type Size = {
  w: number
  h: number
}
type Position = {
  top: number
  left: number
}
type Props = {
  size: Size
  image: HTMLImageElement | null | undefined
  position: Position
  imageSize: Size
  imageContainerSize: Size
  rounded?: boolean
  onClose: (...args: Array<any>) => any
  onApply: (...args: Array<any>) => any
  naturalScale: number
  withImageRecommendations: boolean | null | undefined
}

type ImgixParams = {
  fit?: string
  crop?: string
  mask?: string
  'corner-radius'?: number
  'fp-x'?: number
  'fp-y'?: number
  'fp-z'?: number
  w?: number
  h?: number
}

const Canvas = ({
  size,
  image,
  position,
  imageSize,
  imageContainerSize,
  rounded,
  onClose,
  onApply,
  naturalScale,
  withImageRecommendations,
}: Props): React.ReactNode => {
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null | undefined>(
    null,
  )
  const [showImageRecommendations, setShowImageRecommendations] =
    useState(false)
  const canvasRef = useRef(null)
  useEffect(() => {
    if (canvasRef.current) {
      setCtx(canvasRef.current.getContext('2d'))
    }
  }, [canvasRef.current])

  const apply = () => {
    if (!ctx) return
    let imageX = position.left * -1
    let imageY = position.top * -1

    if (imageSize.h > imageContainerSize.h) {
      imageY += (imageContainerSize.h - imageSize.h) / 2
    }

    if (imageSize.w > imageContainerSize.w) {
      imageX += (imageContainerSize.w - imageSize.w) / 2
    }

    imageX *= naturalScale
    imageY *= naturalScale
    const crop = [
      image,
      imageX,
      imageY,
      imageSize.w * naturalScale,
      imageSize.h * naturalScale,
    ]
    ctx.clearRect(0, 0, size.w, size.h)

    if (rounded) {
      const drawCircle = () => {
        ctx.beginPath()
        ctx.arc(
          (size.w * naturalScale) / 2,
          (size.h * naturalScale) / 2,
          (size.h * naturalScale) / 2,
          0,
          Math.PI * 2,
        )
        ctx.closePath()
        ctx.clip()
      }

      ctx.save()
      drawCircle()
      // call with 5 arguments https://github.com/facebook/flow/blob/main/lib/dom.js#L2365
      // @ts-expect-error
      ctx.drawImage(...crop)
      ctx.beginPath()
      drawCircle()
      ctx.restore()
    } else {
      // @ts-expect-error
      ctx.drawImage(...crop)
    }

    if (canvasRef.current) {
      const base64Image = canvasRef.current.toDataURL('image/jpeg', 0.85)
      const scale = Math.min(imageSize.w / size.w, imageSize.h / size.h)
      const percentX =
        (Math.abs(imageX / naturalScale) + size.w / 2) / imageSize.w
      const percentY =
        (Math.abs(imageY / naturalScale) + size.h / 2) / imageSize.h
      let imgixOptions: ImgixParams = {
        fit: 'crop',
        crop: 'focalpoint',
        'fp-x': percentX,
        'fp-y': percentY,
        'fp-z': scale,
        w: size.w,
        h: size.h,
      }

      if (rounded) {
        imgixOptions = {
          ...imgixOptions,
          mask: 'corners',
          'corner-radius': size.w,
        }
      }

      onApply({
        base64Image,
        imgixOptions,
      })
    }
  }

  return (
    <>
      <canvas
        ref={canvasRef}
        width={`${size?.w * naturalScale}px`}
        height={`${size?.h * naturalScale}px`}
        style={{
          display: 'none',
        }}
      />
      {withImageRecommendations && (
        <ImageRecommendationsButton
          onClick={() => setShowImageRecommendations(true)}
        />
      )}
      <ButtonContainer alignRight>
        <Button onClick={onClose} caption="Cancel" />
        <Button onClick={apply} appearance="primary" caption="Apply" />
      </ButtonContainer>
      {showImageRecommendations && (
        <ImageRecommendations
          onClose={() => setShowImageRecommendations(false)}
        />
      )}
    </>
  )
}

export default Canvas
